import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Typography,
  Button,
  DialogProps,
  PaperProps,
  useTheme,
} from '@mui/material';
import { getTablePreferences, setTablePreferences } from 'components/utils';
import OpenFullPageButton from 'components/common/OpenFullPageButton';

import CloseIcon from '@mui/icons-material/Close';

type Props = {
  open?: boolean;
  onClose?: () => void;
  closeLabel?: string;
  title: string | React.ReactNode;
  fullScreen?: boolean;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  renderContent: () => React.ReactNode;
  renderActions?: () => React.ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  hideCloseButton?: boolean;
  closeButtonStyle?: SxProps;
  sx?: SxProps;
  slotProps?: DialogProps['slotProps'];
  paperProps?: PaperProps & { 'data-testid'?: string };
  extraTitleContent?: React.ReactNode;
  fullPageHref?: string;
  leftFooterActions?: React.ReactNode;
  disableResize?: boolean;
  preferencesTableName?: string;
};

const Modal = ({
  open = true,
  onClose,
  closeLabel = 'Close',
  title,
  size = 'sm',
  renderContent,
  renderActions,
  onSubmit,
  hideCloseButton = false,
  closeButtonStyle,
  fullScreen = false,
  fullWidth = true,
  sx,
  slotProps,
  paperProps,
  extraTitleContent,
  fullPageHref,
  leftFooterActions = null,
  disableResize = false,
  preferencesTableName = 'modal',
}: Props) => {
  const theme = useTheme();

  const [width, setWidth] = useState<number | string>('100%');
  const [isResizing, setIsResizing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      (document.activeElement as HTMLElement)?.blur();
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleResizeStart = (e: React.MouseEvent) => {
    if (disableResize || !fullScreen) return;
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (disableResize || !fullScreen) return;

    const preferences = getTablePreferences(preferencesTableName);

    if (preferences.width) {
      setWidth(preferences.width as number);
    }
  }, [fullScreen, disableResize, preferencesTableName]);

  const stateRef = useRef({ width, isResizing });
  useEffect(() => {
    stateRef.current = { width, isResizing };
  }, [width, isResizing]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { isResizing: resizing } = stateRef.current;

      if (resizing) {
        const calculatedWidth = window.innerWidth - e.clientX;
        const newWidth = Math.min(window.innerWidth, Math.max(300, calculatedWidth));

        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      const { isResizing: resizing, width: finalWidth } = stateRef.current;

      if (resizing && !disableResize) {
        setTablePreferences({
          tableName: preferencesTableName,
          values: [{ key: 'width', value: finalWidth }],
        });
      }
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, disableResize]);

  // Dialog content (unchanged)
  let dialogContent = (
    <React.Fragment>
      <DialogTitle id="modal-title" sx={theme => ({ backgroundColor: theme.palette.background.paper })}>
        <Box
          display="flex"
          alignItems="center"
          width="100%"
          sx={{
            '& > .MuiBox-root:last-child .MuiIconButton-root': {
              marginRight: -1,
            },
          }}
        >
          <Box flexGrow={1} sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: theme => theme.palette.text.primary,
              }}
            >
              {title}
            </Typography>
          </Box>

          {extraTitleContent}

          {fullPageHref ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <OpenFullPageButton href={fullPageHref} />
            </Box>
          ) : null}

          <Box flexShrink={0} sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label="close"
              sx={{
                '&:hover': {
                  backgroundColor: theme => theme.palette.action.hover,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 2,
          backgroundColor: theme => theme.palette.background.default,
          width: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
        dividers={true}
      >
        {renderContent()}
      </DialogContent>

      <DialogActions sx={theme => ({ backgroundColor: theme.palette.background.default, py: 1.5, px: 3 })}>
        {leftFooterActions}

        {!hideCloseButton && (
          <Button onClick={onClose} variant="contained" color="inherit" sx={closeButtonStyle}>
            {closeLabel}
          </Button>
        )}

        {renderActions && renderActions()}
      </DialogActions>
    </React.Fragment>
  );

  if (onSubmit) {
    dialogContent = (
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
        {dialogContent}
      </form>
    );
  }

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="modal-title"
      open={open}
      fullWidth={fullWidth}
      maxWidth={size}
      fullScreen={fullScreen}
      disableEnforceFocus
      disableScrollLock={true}
      sx={{
        ...sx,
        '& .MuiDialog-paper': {
          position: 'relative',
          ...(fullScreen && {
            width: typeof width === 'number' ? `${width}px !important` : `${width} !important`,
            maxWidth: '100vw !important',
            margin: '0 !important',
            marginLeft: 'auto !important',
          }),
        },
      }}
      slotProps={slotProps}
      PaperProps={{
        ...paperProps,
        ref: dialogRef,
      }}
    >
      {fullScreen && !disableResize && (
        <Box
          key="modal-resize-handle"
          onMouseDown={handleResizeStart}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'ew-resize',
            zIndex: theme.zIndex.modal + 1,
          }}
        />
      )}
      <React.Fragment key="modal-content">{dialogContent}</React.Fragment>
    </Dialog>
  );
};

export default Modal;
